import { useApolloClient } from '@apollo/client'
import { getDurationBetweenTwoStringDatesUtils } from '@utils/getDurationBetweenTwoDates.utils'
import { DateTime } from 'luxon'
import { useCallback, useEffect, useState } from 'react'
import { useAuthenticatedQuery } from './useAuth'
import { Booking, BookingStatus } from '@customTypes/booking.interface'
import { GET_USER_BOOKINGS } from '@graphql/queries/getUserBookings.query'

// const fakeBooking: Booking = {
//     lot_id: 'T001',
//     lot_name: 'Edeka',
//     lot_address: 'Naslov',
//     lot_city: 'Munchen 2000',
//     price: 1.01,
//     start_date: '2023-09-20T12:00:00.000Z',
//     end_date: '2023-09-20T14:00:00.000Z',
//     created_at: '2023-09-20T12:00:00.000Z',
//     payment_method: 'Card',
//     booking_ref: '123456',
//     email: 'max.musterman@email.com',
//     plates: [
//         {
//             plate: 'M-1234'
//         }
//     ],
//     lot_description: {
//         de: 'hi'
//     },
//     status: BookingStatus.ACTIVE,
//     bookingType: LotViewName.NIGHTLY
//     // lot_opening_hours: [
//     //     {
//     //         opening_hour: '08:00',
//     //         closing_hour: '20:00',

//     //     }
//     // ]
// }

const getBookingStatus = (startDate: string, endDate: string): BookingStatus => {
    const now = new Date()
    const parsedStartDate = new Date(startDate)
    const parsedEndDate = new Date(endDate)

    // active
    if (now >= parsedStartDate && now <= parsedEndDate) return BookingStatus.ACTIVE
    // pending
    if (now < parsedStartDate) return BookingStatus.FUTURE
    // expired
    if (now > parsedEndDate) return BookingStatus.COMPLETED
    return BookingStatus.CANCELLED
}

const useBookings = () => {
    const [bookings, setBookings] = useState<Booking[]>([])
    const client = useApolloClient()

    const { data: bookingsFromApi, loading } = useAuthenticatedQuery<{
        get_user_bookings: Booking[]
    }>({
        query: GET_USER_BOOKINGS,
        fetchPolicy: 'no-cache'
    })

    const splitAddress = useCallback((address: string): { lot_address: string; lot_city: string } => {
        const addressArray = address.split(', ')
        return { lot_address: addressArray[0], lot_city: addressArray[1] }
    }, [])

    useEffect(() => {
        const parseBookings = async () => {
            if (!bookingsFromApi) return

            const sortedBookings = bookingsFromApi.get_user_bookings.sort((a, b) =>
                a.start_date.localeCompare(b.start_date)
            )

            const parsedBookings = sortedBookings.map((booking: any) => {
                const duration = getDurationBetweenTwoStringDatesUtils(booking.start_date, booking.end_date) || 0

                const startDatetime = DateTime.fromISO(booking.start_date)
                const endDatetime = DateTime.fromISO(booking.end_date)

                let adjustedStartDateTime = startDatetime

                if (endDatetime.offset < startDatetime.offset) {
                    adjustedStartDateTime = startDatetime.minus({ hour: 1 })
                } else if (endDatetime.offset > startDatetime.offset) {
                    adjustedStartDateTime = startDatetime.plus({ hour: 1 })
                }

                return {
                    ...booking,
                    ...splitAddress(booking.lot_address),
                    created_at: booking.created_at_iso_str,
                    status: getBookingStatus(booking.start_date, booking.end_date),
                    hours: duration,
                    hourlyBooking: duration < 23,
                    start_date: adjustedStartDateTime.toISO(),
                    end_date: booking.end_date
                    // any additional fields or changes go here
                }
            })

            setBookings(parsedBookings)
        }
        if (bookingsFromApi) {
            parseBookings()
        } else {
            // error
        }
    }, [bookingsFromApi, client, splitAddress])

    const filterBookingsByStatus = (status: BookingStatus) => {
        return bookings.filter((booking) => booking.status === status)
    }

    const groupBookingsByStatus = () => {
        const activeBookings = filterBookingsByStatus(BookingStatus.ACTIVE)
        const futureBookings = filterBookingsByStatus(BookingStatus.FUTURE)
        const completedBookings = filterBookingsByStatus(BookingStatus.COMPLETED)
        const cancelledBookings = filterBookingsByStatus(BookingStatus.CANCELLED)

        return { activeBookings, futureBookings, completedBookings, cancelledBookings }
    }

    const getBookingByRef = (bookingRef: string) => {
        return bookings.find((booking) => booking.booking_ref === bookingRef)
    }

    return { bookings, loading, filterBookingsByStatus, groupBookingsByStatus, getBookingByRef }
}

export default useBookings
